import type { TFunction } from "i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { useEditZoneEvent } from "@/api/hooks/zone-events/use-edit-zone-event";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { SIZE } from "@/configs/sizes";
import { useLocale } from "@/hooks/use-locale";
import { useSnackbar } from "@/hooks/use-snackbar/use-snackbar";
import { NAMESPACES } from "@/i18n/constants";
import { useOnDemandIrrigationEventTracker } from "@/services/analytics/irrigation/use-on-demand-irrigation-event-tracker";
import { formatTimeAsHoursMinutesAndSeconds } from "@/utils/time";
import { EVENT_DURATION_LOWER_BOUND_MS, EVENT_DURATION_UPPER_BOUND_MS } from "../on-demand-irrigation-constants";
import { FormContainer } from "./form-container";
import { FormValidationMessage } from "./form-validation-message";
import { ResponseTimeHint } from "./response-time-hint";
import { TimeInput } from "./time-input";

const LARGE_STEP_MS = 15 * minuteMs;
const SMALL_STEP_MS = 5 * minuteMs;
const TOOLTIP_DELAY = 150;

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
};

/** Returns an error message if the duration is out of bounds, or null if valid */
function validateEventDuration(
    tOnDemandValidationErrors: TFunction,
    durationMs: number,
    eventStartTime: Date,
    nowMs: number,
): string | null {
    if (durationMs < EVENT_DURATION_LOWER_BOUND_MS)
        return tOnDemandValidationErrors("duration-too-short", {
            minDurationMinutes: EVENT_DURATION_LOWER_BOUND_MS / minuteMs,
        });
    if (durationMs > EVENT_DURATION_UPPER_BOUND_MS)
        return tOnDemandValidationErrors("duration-too-long", {
            maxDurationHours: EVENT_DURATION_UPPER_BOUND_MS / hourMs,
        });

    // New event end time cannot end before current time
    if (eventStartTime.getTime() + durationMs < nowMs)
        return tOnDemandValidationErrors("new-event-ends-before-current-time");
    return null;
}

const roundToNearestMinute = (durationMs: number) => Math.round(durationMs / minuteMs) * minuteMs;

// Ordered left-to-right: [-15m, -5m, +5m, +15m]
const POSITIVE_STEP_BUTTONS = [
    { label: "+5m", value: SMALL_STEP_MS, icon: ChevronRight },
    { label: "+15m", value: LARGE_STEP_MS, icon: ChevronsRight },
] as const;

const NEGATIVE_STEP_BUTTONS = [
    { label: "-15m", value: -LARGE_STEP_MS, icon: ChevronsLeft },
    { label: "-5m", value: -SMALL_STEP_MS, icon: ChevronLeft },
] as const;

interface EditEventFormProps {
    onClose: () => void;
    eventId: string;
    zoneId: string;
    currentEventDuration: number; // ms
    eventEndTime: Date;
    eventStartTime: Date;
    responseTimeMs?: number;
    onPendingNewEndTimeChange?: (newEndTime: Date | null) => void;
}

export function EditEventForm({
    onClose,
    eventId,
    zoneId,
    currentEventDuration,
    eventEndTime,
    eventStartTime,
    responseTimeMs,
    onPendingNewEndTimeChange,
}: EditEventFormProps) {
    const [durationMs, setDurationMs] = useState<number>(roundToNearestMinute(currentEventDuration));
    const onDemandIrrigationEventTracker = useOnDemandIrrigationEventTracker(zoneId);
    const { editZoneEvent } = useEditZoneEvent();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.edit-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });

    const locale = useLocale();
    const durationDeltaMs = durationMs - roundToNearestMinute(currentEventDuration);
    const eventEndTimeMs = eventEndTime.getTime();

    useEffect(() => {
        onPendingNewEndTimeChange?.(new Date(eventEndTimeMs + durationDeltaMs));
    }, [durationDeltaMs, eventEndTimeMs, onPendingNewEndTimeChange]);

    const projectedEndTimeLabel = new Date(eventEndTime.getTime() + durationDeltaMs).toLocaleTimeString(
        locale,
        TIME_FORMAT_OPTIONS,
    );
    const startTimeLabel = eventStartTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const timeDeltaLabel = `${formatTimeAsHoursMinutesAndSeconds(roundToNearestMinute(Math.abs(durationDeltaMs)), { includeSecondsWithMinutes: false })}`;
    const eventDurationLabel =
        durationDeltaMs >= 0
            ? t("buttons.increase-duration", { timeDeltaLabel })
            : t("buttons.decrease-duration", { timeDeltaLabel });
    const validationError = validateEventDuration(tOnDemandValidationErrors, durationMs, eventStartTime, Date.now());

    const modifyDuration = (deltaMs: number) => setDurationMs((prev) => Math.max(0, prev + deltaMs));

    const handleClickEditEvent = async () => {
        const response = await editZoneEvent({
            eventId,
            editEventParams: { duration: durationMs },
        });
        if (response) {
            onDemandIrrigationEventTracker.trackUpdate({ eventId, durationMs });
            enqueueSuccessSnackbar(t("snackbar.success", { timeDeltaLabel }));
            onClose();
        } else {
            enqueueErrorSnackbar(t("snackbar.error"));
            onDemandIrrigationEventTracker.trackUpdateFail(eventId);
        }
    };

    return (
        <FormContainer title={t("title")} onClose={onClose} variant={"edit"}>
            <div className={"flex flex-row gap-2 xs:gap-3 w-full items-center justify-center"}>
                {NEGATIVE_STEP_BUTTONS.map((config) => (
                    <Button
                        key={config.label}
                        tooltip={config.label}
                        tooltipDelay={TOOLTIP_DELAY}
                        size={"icon-xxl"}
                        variant={"outline"}
                        disabled={
                            durationMs + config.value < EVENT_DURATION_LOWER_BOUND_MS ||
                            // Disable if the new event would end before the current time
                            eventStartTime.getTime() + durationMs + config.value < Date.now()
                        }
                        onClick={() => modifyDuration(config.value)}
                    >
                        <Icon iconClassName={"p-0"} icon={config.icon} size={SIZE.LG} />
                    </Button>
                ))}
                <TimeInput
                    className={
                        "py-3 px-2 xs:p-3 w-full min-w-28 max-w-34 flex flex-col items-center gap-1 relative xs:mx-2"
                    }
                    inputClassName={"text-center xs:text-lg xs:tracking-wider"}
                    value={durationMs}
                    onChange={setDurationMs}
                />
                {POSITIVE_STEP_BUTTONS.map((config) => (
                    <Button
                        key={config.label}
                        tooltip={config.label}
                        tooltipDelay={TOOLTIP_DELAY}
                        size={"icon-xxl"}
                        variant={"outline"}
                        disabled={durationMs + config.value > EVENT_DURATION_UPPER_BOUND_MS}
                        onClick={() => modifyDuration(config.value)}
                    >
                        <Icon iconClassName={"p-0"} icon={config.icon} size={SIZE.LG} />
                    </Button>
                ))}
            </div>

            <div className={"flex flex-col w-full gap-1"}>
                <div className={"text-muted-foreground flex flex-row items-center justify-between w-full gap-2"}>
                    <span>{t("event-time")}:</span>
                    <p className={"rounded-lg"}>
                        {startTimeLabel} - {projectedEndTimeLabel}
                    </p>
                </div>
            </div>

            {validationError && <FormValidationMessage errorMessage={validationError} />}

            <div className={"flex flex-col items-center jusity-center gap-1"}>
                <Button
                    className={"w-full p-3"}
                    variant={"info"}
                    disabled={durationDeltaMs === 0 || validationError !== null}
                    onClick={handleClickEditEvent}
                >
                    {eventDurationLabel}
                </Button>
                <ResponseTimeHint responseTimeMs={responseTimeMs} className={"mt-0"} />
            </div>
        </FormContainer>
    );
}
